export const COMPLEX_FIELD_TYPES = [
  'text',
  'textarea',
  'number',
  'date',
  'date_range',
  'time',
  'select',
  'multi_select',
  'checkbox',
  'radio',
  'email',
  'phone',
  'url',
  'file',
] as const

export type ComplexFieldType = (typeof COMPLEX_FIELD_TYPES)[number]
export type ComplexFieldPricingMode =
  | 'none'
  | 'fixed'
  | 'per_unit'
  | 'per_unit_tiered'
  | 'per_unit_percent'

export const COMPLEX_FIELD_TYPE_LABELS: Record<ComplexFieldType, string> = {
  text: 'Text',
  textarea: 'Text lung',
  number: 'Număr',
  date: 'Dată',
  date_range: 'Interval date',
  time: 'Oră',
  select: 'Select (o opțiune)',
  multi_select: 'Multi-select',
  checkbox: 'Checkbox',
  radio: 'Radio',
  email: 'Email',
  phone: 'Telefon',
  url: 'URL',
  file: 'Fișier',
}

export const COMPLEX_FIELD_PRICING_LABELS: Record<ComplexFieldPricingMode, string> = {
  none: 'Fără impact pe preț',
  fixed: 'Sumă fixă',
  per_unit: 'Pe unitate',
  per_unit_tiered: 'Minim + tarif diferit',
  per_unit_percent: 'Procent / unitate',
}

export interface ComplexFieldPricingOptions {
  pricingMinUnits?: number
  pricingExtraAmount?: number
  /** Base / final product price used by per_unit_percent. */
  basePrice?: number
}

export interface ComplexProductFieldDefinition {
  id: number
  name: string
  slug: string
  fieldType: ComplexFieldType
  options?: string[] | null
  pricingMode: ComplexFieldPricingMode
  pricingAmount: number
  pricingMinUnits?: number
  pricingExtraAmount?: number
  pricingUnitLabel?: string | null
  isActive: boolean
  hideFromCustomer?: boolean
  sortOrder: number
}

export interface ProductComplexFieldAssignment {
  id?: number
  productId?: number
  fieldId: number
  isRequired: boolean
  pricingMode?: ComplexFieldPricingMode | null
  pricingAmount?: number | null
  pricingMinUnits?: number | null
  pricingExtraAmount?: number | null
  sortOrder: number
  field?: ComplexProductFieldDefinition
}

export interface CustomFieldValueInput {
  fieldId: number
  value: unknown
}

export interface CustomFieldSnapshot {
  fieldId: number
  name: string
  type: string
  value: unknown
  priceDelta: number
  hideFromCustomer?: boolean
}

/** Inclusive calendar days: 17–17 = 1 zi, 17–18 = 2 zile. */
export function dateRangeDayCount(start: string, end: string): number {
  const startDate = new Date(`${start}T00:00:00Z`)
  const endDate = new Date(`${end}T00:00:00Z`)
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return 0
  const diffMs = endDate.getTime() - startDate.getTime()
  if (diffMs < 0) return 0
  return Math.floor(diffMs / (24 * 60 * 60 * 1000)) + 1
}

export function isEmptyCustomFieldValue(value: unknown): boolean {
  if (value == null) return true
  if (typeof value === 'string') return value.trim() === ''
  if (Array.isArray(value)) return value.length === 0
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>
    if ('start' in record || 'end' in record) {
      return !record.start || !record.end
    }
  }
  return false
}

/** Unit count for number / date_range fields (0 if not applicable). */
export function getFieldUnitCount(fieldType: ComplexFieldType, value: unknown): number {
  if (fieldType === 'number') {
    const num = Number(value)
    if (!Number.isFinite(num) || num <= 0) return 0
    return num
  }

  if (fieldType === 'date_range' && value && typeof value === 'object') {
    const range = value as { start?: string; end?: string }
    if (!range.start || !range.end) return 0
    return dateRangeDayCount(range.start, range.end)
  }

  return 0
}

export function computeFieldPriceDelta(
  fieldType: ComplexFieldType,
  pricingMode: ComplexFieldPricingMode,
  pricingAmount: number,
  value: unknown,
  options?: ComplexFieldPricingOptions,
): number {
  const amount = Number(pricingAmount) || 0
  if (pricingMode === 'none') return 0
  if (pricingMode === 'fixed') return amount

  const units = getFieldUnitCount(fieldType, value)
  if (units <= 0) return 0

  if (pricingMode === 'per_unit') {
    if (amount === 0) return 0
    return amount * units
  }

  if (pricingMode === 'per_unit_tiered') {
    const minUnits = Math.max(0, Math.floor(Number(options?.pricingMinUnits) || 0))
    const extraAmount = Number(options?.pricingExtraAmount) || 0
    if (minUnits <= 0) {
      return extraAmount * units
    }
    if (units <= minUnits) return amount
    return amount + (units - minUnits) * extraAmount
  }

  if (pricingMode === 'per_unit_percent') {
    const basePrice = Number(options?.basePrice) || 0
    if (amount === 0 || basePrice === 0) return 0
    return units * (amount / 100) * basePrice
  }

  return 0
}

export function resolveAssignmentPricing(assignment: ProductComplexFieldAssignment) {
  const field = assignment.field
  const pricingMode = (assignment.pricingMode ?? field?.pricingMode ?? 'none') as ComplexFieldPricingMode
  const pricingAmount = Number(
    assignment.pricingAmount != null ? assignment.pricingAmount : (field?.pricingAmount ?? 0),
  )
  const pricingMinUnits = Number(
    assignment.pricingMinUnits != null
      ? assignment.pricingMinUnits
      : (field?.pricingMinUnits ?? 0),
  )
  const pricingExtraAmount = Number(
    assignment.pricingExtraAmount != null
      ? assignment.pricingExtraAmount
      : (field?.pricingExtraAmount ?? 0),
  )
  return { pricingMode, pricingAmount, pricingMinUnits, pricingExtraAmount }
}

export function computeCustomFieldsPriceDelta(
  assignments: ProductComplexFieldAssignment[],
  values: Record<number, unknown>,
  basePrice = 0,
): number {
  let nonPercentDelta = 0
  const percentItems: Array<{
    fieldType: ComplexFieldType
    pricingAmount: number
    pricingMinUnits: number
    pricingExtraAmount: number
    value: unknown
  }> = []

  for (const assignment of assignments) {
    const field = assignment.field
    if (!field) continue
    const value = values[field.id]
    if (isEmptyCustomFieldValue(value)) continue
    const pricing = resolveAssignmentPricing(assignment)
    if (pricing.pricingMode === 'per_unit_percent') {
      percentItems.push({
        fieldType: field.fieldType,
        pricingAmount: pricing.pricingAmount,
        pricingMinUnits: pricing.pricingMinUnits,
        pricingExtraAmount: pricing.pricingExtraAmount,
        value,
      })
      continue
    }
    nonPercentDelta += computeFieldPriceDelta(
      field.fieldType,
      pricing.pricingMode,
      pricing.pricingAmount,
      value,
      {
        pricingMinUnits: pricing.pricingMinUnits,
        pricingExtraAmount: pricing.pricingExtraAmount,
      },
    )
  }

  const referencePrice = Number(basePrice) + nonPercentDelta
  let percentDelta = 0
  for (const item of percentItems) {
    percentDelta += computeFieldPriceDelta(
      item.fieldType,
      'per_unit_percent',
      item.pricingAmount,
      item.value,
      { basePrice: referencePrice },
    )
  }

  return nonPercentDelta + percentDelta
}

export function buildCustomFieldsKey(customFields?: CustomFieldValueInput[]): string {
  if (!customFields?.length) return ''
  const normalized = [...customFields]
    .map((entry) => ({
      fieldId: Number(entry.fieldId),
      value: entry.value,
    }))
    .filter((entry) => Number.isFinite(entry.fieldId))
    .sort((a, b) => a.fieldId - b.fieldId)
  return JSON.stringify(normalized)
}

function formatDateRo(value: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim())
  if (match) return `${match[3]}.${match[2]}.${match[1]}`
  const date = new Date(value)
  if (!Number.isNaN(date.getTime())) return date.toLocaleDateString('ro-RO')
  return value
}

function complexFieldOptionLabel(item: unknown): string {
  if (typeof item === 'string' || typeof item === 'number') return String(item).trim()
  if (item && typeof item === 'object') {
    const record = item as Record<string, unknown>
    const label = record.label ?? record.value ?? record.name
    if (typeof label === 'string' || typeof label === 'number') return String(label).trim()
  }
  return ''
}

/** Options may arrive as a real array or as a JSON/text string from MariaDB. */
export function normalizeComplexFieldOptions(raw: unknown, depth = 0): string[] {
  if (depth > 3 || raw == null || raw === '') return []

  if (typeof raw === 'string') {
    const trimmed = raw.trim()
    if (!trimmed) return []
    if (
      trimmed.startsWith('[')
      || trimmed.startsWith('{')
      || (trimmed.startsWith('"') && trimmed.endsWith('"'))
    ) {
      try {
        return normalizeComplexFieldOptions(JSON.parse(trimmed), depth + 1)
      } catch {
        // Plain text, one option per line.
      }
    }
    return trimmed.split(/\r?\n/).map((line) => line.trim()).filter(Boolean)
  }

  if (Array.isArray(raw)) {
    return raw.flatMap((item) => {
      const label = complexFieldOptionLabel(item)
      return label ? [label] : []
    })
  }

  if (typeof raw === 'object') {
    return normalizeComplexFieldOptions(Object.values(raw as Record<string, unknown>), depth + 1)
  }

  return []
}

export function formatCustomFieldValue(value: unknown, type?: string): string {
  if (value == null) return '—'
  if (typeof value === 'boolean') return value ? 'Da' : 'Nu'
  if (Array.isArray(value) || type === 'multi_select') {
    const options = normalizeComplexFieldOptions(value)
    return options.length ? options.join(', ') : '—'
  }
  if (type === 'date_range' && typeof value === 'object') {
    const range = value as { start?: string; end?: string }
    if (range.start && range.end) {
      return `${formatDateRo(range.start)} → ${formatDateRo(range.end)}`
    }
  }
  if (type === 'date' && typeof value === 'string' && value.trim()) {
    return formatDateRo(value)
  }
  if (type === 'file' && typeof value === 'string') {
    const name = value.split('/').pop() || value
    try {
      return decodeURIComponent(name)
    } catch {
      return name
    }
  }
  if (typeof value === 'object') {
    const range = value as { start?: string; end?: string }
    if (range.start && range.end) {
      return `${formatDateRo(range.start)} → ${formatDateRo(range.end)}`
    }
    try {
      return JSON.stringify(value)
    } catch {
      return '—'
    }
  }
  return String(value)
}

export function isCustomFieldImageValue(value: unknown, type?: string): boolean {
  if (type !== 'file' || typeof value !== 'string' || !value) return false
  return /\.(png|jpe?g|webp|gif|svg|avif|heic|heif)(\?.*)?$/i.test(value)
}
